import React, { useEffect, useState } from "react";
import {
  Container,
  Row,
  Col,
  Form,
  Button,
  OverlayTrigger,
  Tooltip,
} from "react-bootstrap";
import { useParams, useNavigate } from "react-router-dom";
import { useAuthStore } from "../../../zustand/loginStore";
import {
  activateCategory,
  createAuthenticatedAxiosInstance,
  deactivateCategory,
  fetchCategoryById,
  updateCategory,
} from "api/auth";
import CustomNavbar from "components/users/CustomNavbar";
import CustomBackButton from "components/general/CustomBackButton";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCheckCircle,
  faExclamationTriangle,
  faInfoCircle,
  faPlus,
  faQuestionCircle,
  faTimesCircle,
  faTrash,
} from "@fortawesome/free-solid-svg-icons";
import LoadingScreen from "components/general/LoadingScreen";
import logo from "assets/logo.png";
import ImageModal from "components/general/ImageModal";
import { Category } from "interfaces/ICategory";
import "./CategoryDetails.scss";
import ConfirmationModal from "components/general/ConfirmationModal";
import CancelContinue from "components/general/CancelContinueModal";
import { convertToBase64 } from "utils/imagesAndColors";
import { primaryColor, secondaryColor } from "utils/colors";

const CategoryDetails: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { token } = useAuthStore();
  const axiosInstance = createAuthenticatedAxiosInstance(token);
  axiosInstance.defaults.headers["Cache-Control"] = "no-cache";
  axiosInstance.defaults.headers["Pragma"] = "no-cache";
  const [category, setCategory] = useState<Category | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [showToggleModal, setShowToggleModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState("Error al conectar con el servidor. Inténtalo más tarde.");
  const [isEditing, setIsEditing] = useState(false);
  const [editedName, setEditedName] = useState("");
  const [editedImage, setEditedImage] = useState("");
  const [imageKey, setImageKey] = useState(Date.now());
  const [deletedImages, setDeletedImages] = useState<string[]>([]);
  const [hasNewImage, setHasNewImage] = useState(false);

  /**
   * Effect hook to fetch and load category data
   * @effect
   * @dependencies [id, token]
   *
   * The effect:
   * 1. Makes API call to fetch category by ID
   * 2. Updates category state with response data
   * 3. Manages loading state
   * 4. Handles error cases
   */
  useEffect(() => {
    const loadCategory = async () => {
      try {
        const response = await fetchCategoryById(axiosInstance, Number(id));
        if (response.images && response.images.length > 0) {
          response.images = response.images.map(
            (img: string) => `${img}?t=${Date.now()}`
          );
        }
        setCategory(response);
        setIsLoading(false);
      } catch (error) {
        console.error("Error fetching category:", error);
        setIsLoading(false);
      }
    };

    loadCategory();
  }, [id, token]);

  /**
   * Effect hook to initialize form data when category is loaded
   * @effect
   * @dependencies [category]
   *
   * Sets the initial name and image from category data when no new image has been selected
   */
  useEffect(() => {
    if (category && !hasNewImage) {
      setEditedName(category.name);
      setEditedImage(category.images[0]);
    }
  }, [category]);

  /**
   * Handles cancellation of editing mode
   * @function handleCancel
   *
   * Resets all form fields to their original values and exits editing mode
   */
  const handleCancel = () => {
    setIsEditing(false);
    setEditedName(category?.name || "");
    setEditedImage(category?.images[0] || "");
    setDeletedImages([]);
    setHasNewImage(false);
    setImageKey(Date.now());
  };

  /**
   * Enables editing mode for the category
   * @function handleEdit
   */
  const handleEdit = () => {
    setIsEditing(true);
  };

  /**
   * Shows the modal for toggling category status
   * @function handleToggleStatus
   */
  const handleToggleStatus = () => {
    setShowToggleModal(true);
  };

  /**
   * Handles the confirmation of category status toggle
   * @function handleConfirmToggle
   * @async
   *
   * Activates or deactivates the category based on current status
   * Updates UI state based on the response
   */
  const handleConfirmToggle = async () => {
    if (!category) return;

    try {
      const axiosInstance = createAuthenticatedAxiosInstance(token);
      let response;

      if (category.status === 1) {
        response = await deactivateCategory(axiosInstance, category.id);
      } else {
        response = await activateCategory(axiosInstance, category.id);
      }

      if (response.message) {
        setCategory({ ...category, status: category.status === 1 ? 0 : 1 });
        setShowToggleModal(false);
        setShowSuccessModal(true);
      } else {
        setShowErrorModal(true);
      }
    } catch (error: any) {
      console.error("Error toggling category status:", error);
      
      // Manejar error específico cuando no se puede desactivar por productos con stock
      if (error.response?.status === 400 && error.response?.data?.error === "No se puede desactivar la categoría") {
        const errorData = error.response.data;
        const productList = errorData.products_with_stock?.join(", ") || "productos";
        const totalProducts = errorData.total_products || 0;
        
        const specificErrorMessage = `No se puede desactivar la categoría.\n\nExisten ${totalProducts} producto(s) con esta categoría que tienen stock disponible:\n${productList}\n\nPor favor, agota el stock de estos productos antes de desactivar la categoría.`;
        setErrorMessage(specificErrorMessage);
        setShowErrorModal(true);
      } else {
        setErrorMessage("Error al conectar con el servidor. Inténtalo más tarde.");
        setShowErrorModal(true);
      }
    }
  };

  /**
   * Handles saving category changes
   * @function handleSave
   * @async
   *
   * Updates category with new name and image
   * Manages deleted images with proper server-side naming convention
   */
  const handleSave = async () => {
    try {
      const response = await updateCategory(axiosInstance, Number(id), {
        name: editedName,
        images: editedImage ? [editedImage] : [],
        deleted_images: deletedImages.map(
          (filename) => `${category?.id}_img1.png`
        ),
      });

      if (response) {
        setCategory({
          ...category!,
          name: editedName,
          images: editedImage ? [editedImage] : [],
        });
        setDeletedImages([]);
        setImageKey(Date.now());
        setIsEditing(false);
        setShowSuccessModal(true);
      }
    } catch (error) {
      setShowErrorModal(true);
    }
  };

  /**
   * Handles image deletion
   * @function handleDeleteImage
   *
   * Marks the current image for deletion and clears the image preview
   */
  const handleDeleteImage = () => {
    if (category?.images[0]) {
      setDeletedImages([`img1`]);
    }
    setEditedImage("");
  };

  /**
   * Handles new image selection
   * @function onImageSelect
   * @async
   * @param {React.ChangeEvent<HTMLInputElement>} event - The file input change event
   *
   * Converts selected image to base64 and updates the form state
   */
  const onImageSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (files && files[0]) {
      const base64Image = await convertToBase64(files[0]);
      setEditedImage(base64Image);
      setHasNewImage(true);
      setImageKey(Date.now());
      event.target.value = "";
    }
  };

  return (
    <>
      <CustomNavbar />
      <CustomBackButton onClick={() => navigate("/inventories/categories")} />
      <Container
        className="px-4 px-md-5  category-details"
        style={{ maxWidth: "1000px" }}
      >
        <Row className="justify-content-center mb-4">
          <Col xs={12} className="text-center">
            <img src={logo} alt="Logo" style={{ width: "175px" }} />
          </Col>
        </Row>
        <Row className="mb-4">
          <Col className="text-center">
            <h1>Detalles de la Categoría</h1>
          </Col>
        </Row>

        {isLoading ? (
          <LoadingScreen loadingText="Cargando información de la categoría..." />
        ) : (
          <>
            <Row className="mb-3">
              <Col xs={6}>
                <Form.Label className="fw-bold">No.</Form.Label>
                <div className="d-flex align-items-center">
                  <span style={{ fontSize: "24px", color: "#747775" }}>
                    {category?.id}
                  </span>
                  <OverlayTrigger
                    trigger={["hover", "focus"]}
                    placement="right"
                    overlay={
                      <Tooltip className="custom-tooltip">
                        ID único de la categoría
                      </Tooltip>
                    }
                  >
                    <FontAwesomeIcon icon={faInfoCircle} className="ms-2" />
                  </OverlayTrigger>
                </div>
              </Col>
              <Col xs={6} className="text-end">
                <OverlayTrigger
                  placement="left"
                  overlay={
                    <Tooltip className="custom-tooltip">
                      Estado actual de la categoría
                    </Tooltip>
                  }
                >
                  <span
                    className={`status-badge ${
                      category?.status ? "active" : "inactive"
                    }`}
                  >
                    {category?.status ? "Activo" : "Inactivo"}
                  </span>
                </OverlayTrigger>
              </Col>
            </Row>

            <Form>
              <Form.Group className="mb-4">
                <Form.Label className="fw-bold">
                  Nombre de la categoría
                </Form.Label>
                <Form.Control
                  type="text"
                  onChange={(e) => setEditedName(e.target.value)}
                  value={editedName}
                  readOnly={!isEditing}
                  placeholder="Nombre de la categoría"
                />
              </Form.Group>

              <Form.Group className="mb-4">
                <Form.Label className="fw-bold">
                  Imagen representativa
                  <OverlayTrigger
                    placement="right"
                    overlay={
                      <Tooltip className="custom-tooltip">
                        Imagen que identifica la categoría
                      </Tooltip>
                    }
                  >
                    <FontAwesomeIcon icon={faQuestionCircle} className="ms-2" />
                  </OverlayTrigger>
                </Form.Label>

                <div className="d-flex justify-content-center">
                  {editedImage ? (
                    <div
                      style={{
                        width: "9.5rem",
                        height: "9.5rem",
                        borderRadius: "9px",
                        overflow: "hidden",
                        position: "relative",
                        cursor: "pointer",
                      }}
                      onClick={() => setModalOpen(true)}
                    >
                      <img
                        key={imageKey}
                        src={
                          editedImage.startsWith("data:image")
                            ? editedImage
                            : editedImage.startsWith("http")
                            ? editedImage
                            : `data:image/jpeg;base64,${editedImage}`
                        }
                        alt="Category"
                        style={{
                          width: "100%",
                          height: "100%",
                          objectFit: "cover",
                        }}
                      />
                      {isEditing && (
                        <Button
                          variant="link"
                          style={{
                            position: "absolute",
                            top: "0.1rem",
                            right: "0.1rem",
                            color: "#614900",
                            borderRadius: "50%",
                            padding: "8px",
                            fontSize: "1.2rem",
                            zIndex: 10,
                          }}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteImage();
                          }}
                        >
                          <FontAwesomeIcon icon={faTrash} />
                        </Button>
                      )}
                    </div>
                  ) : (
                    isEditing && (
                      <label style={{ cursor: "pointer" }}>
                        <div
                          style={{
                            border: "2px solid" + secondaryColor,
                            borderRadius: "9px",
                            backgroundColor: secondaryColor,
                            width: "9.5rem",
                            height: "9.5rem",
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                          }}
                        >
                          <div
                            style={{
                              backgroundColor: primaryColor,
                              borderRadius: "50%",
                              width: "8rem",
                              height: "8rem",
                              display: "flex",
                              justifyContent: "center",
                              alignItems: "center",
                            }}
                          >
                            <FontAwesomeIcon
                              icon={faPlus}
                              color="white"
                              style={{ height: "7rem", fontWeight: "lighter" }}
                            />
                          </div>
                          <input
                            type="file"
                            accept=".jpg,.jpeg,.png"
                            onChange={onImageSelect}
                            style={{ display: "none" }}
                          />
                        </div>
                      </label>
                    )
                  )}
                </div>
              </Form.Group>
            </Form>

            <Row className="mt-4 mb-5">
              <Col>
                <Button
                  className="bg-secundary"
                  variant="primary"
                  onClick={isEditing ? handleCancel : handleToggleStatus}
                >
                  {isEditing
                    ? "Cancelar"
                    : category?.status
                    ? "Desactivar categoría"
                    : "Activar categoría"}
                </Button>
              </Col>
              <Col className="text-end">
                <Button
                  variant="primary"
                  onClick={isEditing ? handleSave : handleEdit}
                >
                  {isEditing ? "Guardar categoría" : "Editar categoría"}
                </Button>
              </Col>
            </Row>
          </>
        )}
      </Container>

      <ImageModal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        images={editedImage ? [editedImage] : category?.images || []}
        selectedIndex={0}
        onSelectImage={() => {}}
        isReadOnly={true}
        isSingleImage={true}
      />
      <CancelContinue
        show={showToggleModal}
        onHide={() => setShowToggleModal(false)}
        onConfirm={handleConfirmToggle}
        title={`${category?.status ? "Desactivar" : "Activar"} categoría`}
        message={`¿Estás seguro que deseas ${
          category?.status ? "desactivar" : "activar"
        } esta categoría?`}
        icon={faExclamationTriangle}
        iconColor="text-danger"
      />

      <ConfirmationModal
        show={showSuccessModal}
        onHide={() => setShowSuccessModal(false)}
        title="Confirmación"
        message="Los cambios se han guardado con éxito"
        icon={faCheckCircle}
        iconColor="text-success"
      />

      <ConfirmationModal
        show={showErrorModal}
        onHide={() => setShowErrorModal(false)}
        title="Error"
        message={errorMessage}
        icon={faTimesCircle}
        iconColor="text-danger"
      />
    </>
  );
};

export default CategoryDetails;
